// Mall cart — single outlet per cart (v1). Stored in localStorage so guests
// can build a cart before signing in; validated server-side at checkout.
export interface MallCartItem {
  stockId: string;
  code: string;
  name: string;
  price: number;
  quantity: number;
  imageUrl?: string;
}

export interface MallCart {
  storeCode: string;
  storeName: string;
  items: MallCartItem[];
}

const CART_KEY = 'ppos_mall_cart';

export function loadCart(): MallCart | null {
  try {
    const raw = localStorage.getItem(CART_KEY);
    if (!raw) return null;
    const cart = JSON.parse(raw) as MallCart;
    if (!cart?.storeCode || !Array.isArray(cart.items)) return null;
    return cart;
  } catch {
    return null;
  }
}

export function saveCart(cart: MallCart | null) {
  try {
    if (!cart || cart.items.length === 0) {
      localStorage.removeItem(CART_KEY);
    } else {
      localStorage.setItem(CART_KEY, JSON.stringify(cart));
    }
    window.dispatchEvent(new CustomEvent('mall-cart-changed'));
  } catch {
    /* ignore */
  }
}

export function cartCount(cart: MallCart | null): number {
  return cart ? cart.items.reduce((n, i) => n + i.quantity, 0) : 0;
}

/** Add an item. Returns 'conflict' when the cart holds a different outlet. */
export function addToCart(
  store: { code: string; name: string },
  item: { stockId: string; code: string; name: string; price: number; imageUrl?: string },
  quantity = 1,
): 'ok' | 'conflict' {
  const existing = loadCart();
  if (existing && existing.storeCode !== store.code) return 'conflict';
  const cart: MallCart = existing ?? { storeCode: store.code, storeName: store.name, items: [] };
  const line = cart.items.find((i) => i.stockId === item.stockId);
  if (line) {
    line.quantity += quantity;
  } else {
    cart.items.push({ ...item, quantity });
  }
  saveCart(cart);
  return 'ok';
}

export function updateQuantity(stockId: string, quantity: number) {
  const cart = loadCart();
  if (!cart) return;
  const line = cart.items.find((i) => i.stockId === stockId);
  if (!line) return;
  line.quantity = Math.max(1, quantity);
  saveCart(cart);
}

export function removeItem(stockId: string) {
  const cart = loadCart();
  if (!cart) return;
  cart.items = cart.items.filter((i) => i.stockId !== stockId);
  saveCart(cart);
}
